import { DatePicker, Flex, Form, FormProps, Input, InputNumber, Radio } from 'antd';
import { OptionProps } from 'antd/es/select';
import dayjs from 'dayjs';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import AntdSelect from '@/components/general/AntdSelect/AntdSelect';
import LinkTextField from '@/components/general/LinkTextField';
import { useUserContext } from '@/contexts/UserContext';
import { USER_WORK_AREA } from '@/model/General/UserWorkArea';
import { PackageItem } from '@/model/Logistic/PackageItem/PackageItem';
import { ProductStatusStrings } from '@/model/Logistic/PackageItem/ProductStatus';
import { ProductTypeStrings } from '@/model/Logistic/PackageItem/ProductTypes';
import { PACKAGE_STATES } from '@/model/Logistic/PackageStates';
import {
  SHIPPING_ITEM_REFERENCE,
  ShippingItemReferenceString,
} from '@/model/Logistic/Shipment/ShipmentReference';

import usePackageItemForm from './usePackageItemForm';

interface ShipmentPackageItemFormProps {
  formProps: FormProps;
  editingPackageItem?: PackageItem;
  canSellItems?: boolean;
  movilLink?: string;
  createMode: boolean;
}

const ShipmentPackageItemForm = (props: ShipmentPackageItemFormProps) => {
  const { t } = useTranslation();
  const { userWorkArea } = useUserContext();
  const { formProps, editingPackageItem, canSellItems, movilLink, createMode } = props;
  const { productType, productsSelectProps, serialNumbersSelectProps } = usePackageItemForm(
    formProps,
    editingPackageItem,
  );
  const elementFormName = useMemo(() => {
    if (productType === 1) return 'unidad';
    if (productType === 2) return 'device';
  }, [productType]);
  const elementFormPlaceholder = useMemo(() => {
    if (productType === 0) return t('Seleccione un producto');
    if (productType === 1) return t('Seleccione una unidad');
    if (productType === 2) return t('Seleccione un dispositivo');

    return t('Seleccione un tipo de contenido');
  }, [productType, t]);
  // Package status
  const itemPackageStatus = useMemo(
    () =>
      typeof editingPackageItem?.package != 'number'
        ? editingPackageItem?.package?.status
        : undefined,
    [editingPackageItem],
  );
  // Form watchers
  const itemShippedReference = Form.useWatch('item_shipped_reference', formProps?.form);
  const inWarrantyPeriod = Form.useWatch('in_warranty_period', formProps?.form);

  return (
    <>
      <fieldset>
        <legend>{t('Información del producto')}</legend>
        <Form.Item label={t('Tipo de contenido')} name="product_type">
          <AntdSelect
            options={Object.values(ProductTypeStrings).map((v, index) => ({
              label: t(v),
              value: index,
            }))}
            disabled={!createMode && !!editingPackageItem}
            allowClear
          />
        </Form.Item>
        <Form.Item
          label={t('Producto')}
          name={'product'}
          rules={[{ required: true, message: elementFormPlaceholder }]}>
          <AntdSelect
            {...productsSelectProps}
            disabled={productType === undefined || (!createMode && !!editingPackageItem)}
            placeholder={elementFormPlaceholder}
            onChange={(_, opt) =>
              !Array.isArray(opt) &&
              formProps?.form?.setFieldValue('name', opt?.label ?? opt?.value)
            }
            allowClear
            showSearch
          />
        </Form.Item>
        {!createMode && (productType === 1 || productType === 2) && (
          <>
            <Form.Item label={t('Número de serie')} name={elementFormName}>
              <AntdSelect
                {...serialNumbersSelectProps}
                placeholder={elementFormPlaceholder}
                allowClear
                showSearch
                onChange={(v, opt) => {
                  serialNumbersSelectProps.onChange?.(v, opt);
                  formProps?.form?.setFieldValue('serial_number', (opt as OptionProps)?.label);
                }}
              />
            </Form.Item>
            <Form.Item hidden label={t('Número de serie')} name={'serial_number'}>
              <Input />
            </Form.Item>
          </>
        )}
        <Form.Item
          label={t('Estado')}
          name="item_shipped_state"
          rules={[{ required: true, message: t('Seleccione un estado') }]}>
          <Radio.Group>
            {Object.entries(ProductStatusStrings).map(([key, value]) => (
              <Radio key={key} value={parseInt(key)}>
                {t(value)}
              </Radio>
            ))}
          </Radio.Group>
        </Form.Item>
      </fieldset>
      <fieldset>
        <legend>{t('Información del envío')}</legend>
        <Form.Item
          label={t('Cantidad solicitada')}
          name="requested_amount"
          rules={[{ required: true }]}>
          <InputNumber style={{ width: '100%' }} disabled={productType != 0} min={0} />
        </Form.Item>

        {!createMode &&
          itemPackageStatus != undefined &&
          itemPackageStatus != PACKAGE_STATES.PENDIENTE && (
            <Form.Item label={t('Cantidad enviada')} name="execute_amount">
              <InputNumber style={{ width: '100%' }} min={0} />
            </Form.Item>
          )}
        <Form.Item
          label={t('Referencia de envío')}
          name={'item_shipped_reference'}
          rules={[{ required: true, message: t('Seleccione una referencia de envío') }]}>
          <Radio.Group>
            {Object.entries(ShippingItemReferenceString).map(([key, value]) => {
              if (!canSellItems && parseInt(key) === SHIPPING_ITEM_REFERENCE.VENTA_REPUESTO) {
                return;
              }

              // Mantengo la opción de "Sin devolución" solo para el área de logística, ya que es una referencia que se utiliza para casos específicos de gestión logística.
              if (
                userWorkArea != USER_WORK_AREA.LOGISTICS &&
                parseInt(key) === SHIPPING_ITEM_REFERENCE.SIN_DEVOLUCION
              ) {
                return;
              }

              return (
                <Radio key={key} value={parseInt(key)}>
                  {t(value)}
                </Radio>
              );
            })}
          </Radio.Group>
        </Form.Item>
      </fieldset>

      {(itemShippedReference === SHIPPING_ITEM_REFERENCE.SUJETO_PERITAJE ||
        itemShippedReference === SHIPPING_ITEM_REFERENCE.PREVENTIVO ||
        itemShippedReference === SHIPPING_ITEM_REFERENCE.SIN_DEVOLUCION) && (
        <Form.Item
          label={t('En período de garantía')}
          name="in_warranty_period"
          rules={[{ required: true, message: t('Seleccione estado de garantía') }]}>
          <Radio.Group>
            <Radio key={'warranty_period_yes'} value={true}>
              {t('Sí')}
            </Radio>
            <Radio key={'warranty_period_no'} value={false}>
              {t('No')}
            </Radio>
          </Radio.Group>
        </Form.Item>
      )}
      {inWarrantyPeriod && (
        <Flex vertical style={{ marginBottom: 24 }}>
          <Form.Item
            style={{ marginBottom: 0 }}
            label={t('Fecha de inicio de garantía')}
            name="warranty_start_date"
            rules={[{ required: true, message: t('Seleccione la feceha de inicio de garantía') }]}
            getValueProps={value => ({
              value: value ? dayjs(value) : undefined,
            })}>
            <DatePicker format={'L'} placeholder={t('Seleccionar una fecha')} />
          </Form.Item>
          {movilLink && <LinkTextField label={t('Link a móvil')} to={movilLink} external />}
        </Flex>
      )}
    </>
  );
};

export default ShipmentPackageItemForm;
